import type { ReactNode } from "react";
import {
  Radio as AriaRadio,
  RadioGroup as AriaRadioGroup,
  type RadioGroupProps as AriaRadioGroupProps,
  Label,
  Text,
} from "react-aria-components";
import { cx } from "@/lib/cx";
import { FieldDescription, FieldErrorText, fieldRoot } from "./field";

export interface RadioGroupProps extends Omit<AriaRadioGroupProps, "className" | "children"> {
  label: string;
  /** Keep the label for assistive tech only (the section heading above already says it). */
  labelHidden?: boolean;
  description?: ReactNode;
  error?: string;
  className?: string;
  children: ReactNode;
}

/** Group label, optional description and error, options stacked with a small gap. Compose with <Radio>. */
export function RadioGroup({ label, labelHidden, description, error, className, children, ...props }: RadioGroupProps) {
  return (
    <AriaRadioGroup className={cx(fieldRoot, className)} isInvalid={error ? true : props.isInvalid} {...props}>
      <Label className={labelHidden ? "sr-only" : "text-sm font-medium text-fg"}>{label}</Label>
      {description ? <FieldDescription>{description}</FieldDescription> : null}
      <div className="flex flex-col gap-2">{children}</div>
      <FieldErrorText>{error}</FieldErrorText>
    </AriaRadioGroup>
  );
}

/**
 * Choice card: a bordered row with the radio dot, a label and a description. The selected card gets
 * the accent border and tint; focus draws the standard ring. `children` below the text (a preview)
 * stays inside the card.
 */
export function Radio({
  label,
  description,
  preview,
  ...props
}: {
  value: string;
  label: string;
  description?: string;
  preview?: ReactNode;
  isDisabled?: boolean;
}) {
  return (
    <AriaRadio
      {...props}
      className={cx(
        "group transition-ui flex cursor-default items-start gap-3 rounded-control border border-input-border bg-input-bg p-3",
        "data-hovered:border-input-border-hover data-selected:border-accent data-selected:bg-accent-soft data-disabled:opacity-50",
        "data-focus-visible:outline-2 data-focus-visible:outline-solid data-focus-visible:outline-focus data-focus-visible:outline-offset-1",
      )}
    >
      <span
        aria-hidden="true"
        className="mt-0.5 flex size-4 shrink-0 items-center justify-center rounded-full border border-input-border bg-input-bg group-data-selected:border-accent group-data-selected:bg-accent"
      >
        <span className="size-1.5 rounded-full bg-accent-fg opacity-0 group-data-selected:opacity-100" />
      </span>
      <span className="flex min-w-0 flex-1 flex-col gap-0.5">
        <span className="text-sm font-medium text-fg">{label}</span>
        {description ? (
          <Text
            slot="description"
            className="text-xs text-pretty text-fg-muted group-data-selected:text-accent-soft-fg"
          >
            {description}
          </Text>
        ) : null}
        {preview ? <span className="mt-2">{preview}</span> : null}
      </span>
    </AriaRadio>
  );
}
